// API Service for Dashboard
import { ebitaFetcher, APIUrls } from 'src/app/helpers/connection/API'

// API Response Interfaces
interface UserTasksSummary {
  tasksCount: number
  newTasksCount: number
  newTaskLastReceivedDateTime: string
  completedTasksCountToday: number
  urgentTasksCount: number
  inProgressTasksCount: number
  tasksTAT: {
    totalTasksOnTime: number
    totalTasksExceed: number
  }
}

interface MonthlySummary {
  month: number
  year: number
  totalTasksOnTime: number
  totalTasksExceed: number
}

interface UserTasksTATHistorySummary {
  monthlySummaries: MonthlySummary[]
}

interface PriorityCounts {
  NORMAL: number
  LOW: number
  MEDIUM: number
  HIGH: number
}

interface UserTasksPrioritySummary {
  priorityCounts: PriorityCounts
}

interface ProcessTaskSummary {
  processDesignCode: string
  processDesignName: string
  tasksCount: number
  newTasksCount: number
  completedTasksCount: number
  activeTasksCount: number
  tatExceededCount: number
  tatOnTimeCount: number
  averageTATDays: number
}

interface ProcessTasksSummary {
  processTaskSummaries: ProcessTaskSummary[]
}

interface RankingSummary {
  departmentCode: string
  departmentName: string
  userName: string
  name: string
  completedTasksCount: number
}

interface ProcessTasksAchievementRankSummary {
  rankingSummaries: RankingSummary[]
}

interface DashboardSummaryResponse {
  userTasksSummary: UserTasksSummary
  userTasksTATHistorySummary: UserTasksTATHistorySummary
  userTasksPrioritySummary: UserTasksPrioritySummary
  processTasksSummary: ProcessTasksSummary
  processTasksAchievementRankSummary: ProcessTasksAchievementRankSummary
}

// Component Data Interfaces
export interface StatsCardData {
  title: string;
  value: number;
  subtitle?: string;
  color: string;
}

export interface TurnAroundTimeData {
  month: string;
  exceed: number;
  within: number;
}

export interface DepartmentData {
  name: string;
  value: number;
  color: string;
}

export interface PriorityTask {
  priority: string;
  tasks: number;
}

export interface LibraryRecord {
  libraryName: string;
  rec: string;
  doc: string;
}

export interface ProcessData {
  name: string;
  newJob: number;
  totalActive: number;
  completedJobs: string;
  turnAroundTime: string;
  averageAgeing: number;
}

export interface DepartmentPerformance {
  department: string;
  highestCompletion: string;
}

export interface DepartmentPerson {
  name: string;
  email: string;
  extension: string;
}

export interface DepartmentPersonnel {
  departmentName: string;
  users: DepartmentPerson[];
}

// Export the response type
export type { DashboardSummaryResponse }

// All Dashboard Data Interface
export interface AllDashboardData {
  statsCards: StatsCardData[]
  turnAroundTimeData: TurnAroundTimeData[]
  monthlyPerformance: { month: string; performance: string }[]
  departmentDistribution: DepartmentData[]
  priorityTasks: PriorityTask[]
  processData: ProcessData[]
  departmentPerformance: DepartmentPerformance[]
}

// Single API call to fetch all dashboard data
export const fetchAllDashboardData = async (): Promise<AllDashboardData> => {
  try {
    const response = await ebitaFetcher.get<DashboardSummaryResponse>(APIUrls.Dashboard.Summary)
    const data = response.data
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    // Transform all data
    const statsCards: StatsCardData[] = [
      {
        title: 'Completed Tasks Today',
        value: data.userTasksSummary.completedTasksCountToday,
        color: '#3699FF',
      },
      {
        title: 'New Tasks',
        value: data.userTasksSummary.newTasksCount,
        subtitle: 'Received Last 2 Days',
        color: '#F64E60',
      },
      {
        title: 'Urgent Tasks',
        value: data.userTasksSummary.urgentTasksCount,
        color: '#FFA800',
      },
      {
        title: 'Total Active Tasks',
        value: data.userTasksSummary.inProgressTasksCount,
        color: '#1BC5BD',
      },
      {
        title: 'Turn Around Time',
        value: data.userTasksSummary.tasksTAT.totalTasksExceed,
        subtitle: `${data.userTasksSummary.tasksTAT.totalTasksExceed} / ${data.userTasksSummary.tasksTAT.totalTasksOnTime}`,
        color: '#8950FC',
      },
    ]

    const turnAroundTimeData: TurnAroundTimeData[] = data.userTasksTATHistorySummary.monthlySummaries.map((summary) => ({
      month: monthNames[summary.month - 1],
      exceed: summary.totalTasksExceed,
      within: summary.totalTasksOnTime,
    }))

    const monthlyPerformance = data.userTasksTATHistorySummary.monthlySummaries.map((summary) => {
      const monthName = monthNames[summary.month - 1]
      const yearShort = summary.year.toString().slice(-2)
      const total = summary.totalTasksOnTime + summary.totalTasksExceed
      
      return {
        month: `${monthName} '${yearShort}`,
        performance: `${summary.totalTasksOnTime} / ${total}`,
      }
    })

    // Group by department and sum completed tasks
 

      const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'urgent':
        return '#F64E60';
      case 'low':
        return '#FFA800';
      case 'medium':
        return '#3699FF';
      case 'normal':
        return '#1BC5BD';
      default:
        return '#7E8299';
    }
  };

    const priorityTasks: PriorityTask[] = [
      { priority: 'Urgent', tasks: data.userTasksPrioritySummary.priorityCounts.HIGH || 0 },
      { priority: 'Medium', tasks: data.userTasksPrioritySummary.priorityCounts.MEDIUM || 0 },
      { priority: 'Low', tasks: data.userTasksPrioritySummary.priorityCounts.LOW || 0 },
      { priority: 'Normal', tasks: data.userTasksPrioritySummary.priorityCounts.NORMAL || 0 },
    ]

      const departmentDistribution: DepartmentData[] = priorityTasks.map((task, index) => ({
          name: task.priority,
          value: task.tasks,
          color: getPriorityColor(task.priority),
        }))
    const processData: ProcessData[] = data.processTasksSummary.processTaskSummaries.map((process) => ({
      name: process.processDesignName,
      newJob: process.newTasksCount,
      totalActive: process.activeTasksCount,
      completedJobs: `${process.completedTasksCount}/${process.tasksCount}`,
      turnAroundTime: `${process.tatOnTimeCount}/${process.tatExceededCount}`,
      averageAgeing: Math.round(process.averageTATDays * 10) / 10,
    }))
    const departmentPerformance: DepartmentPerformance[] = data.processTasksAchievementRankSummary.rankingSummaries.map((summary) => ({
      department: summary.departmentName,
      highestCompletion: summary.name,
    }))

    return {
      statsCards,
      turnAroundTimeData,
      monthlyPerformance,
      departmentDistribution,
      priorityTasks,
      processData,
      departmentPerformance,
    }
  } catch (error) {
    console.error('Error fetching dashboard data:', error)
    throw error
  }
}

// Legacy functions for backward compatibility (now unused)
export const fetchStatsCards = async (): Promise<StatsCardData[]> => {
  const data = await fetchAllDashboardData()
  return data.statsCards
}

export const fetchTurnAroundTimePerformance = async (): Promise<TurnAroundTimeData[]> => {
  const data = await fetchAllDashboardData()
  return data.turnAroundTimeData
}

export const fetchDepartmentDistribution = async (): Promise<DepartmentData[]> => {
  const data = await fetchAllDashboardData()
  return data.departmentDistribution
}

export const fetchMonthlyPerformance = async () => {
  const data = await fetchAllDashboardData()
  return data.monthlyPerformance
}

export const fetchPriorityTasks = async (): Promise<PriorityTask[]> => {
  const data = await fetchAllDashboardData()
  return data.priorityTasks
}

export const fetchLibraryRecords = async (): Promise<LibraryRecord[]> => {
  try {
    const response = await ebitaFetcher.get(APIUrls.Dashboard.LibrarySummary)
    const data = response.data
    return data
    } catch (error) {
    console.error('Error fetching library data:', error)
    throw error
  }
};

export const fetchProcessData = async (): Promise<ProcessData[]> => {
  const data = await fetchAllDashboardData()
  return data.processData
};

export const fetchDepartmentPerformance = async (): Promise<DepartmentPerformance[]> => {
  const data = await fetchAllDashboardData()
  return data.departmentPerformance
};

export const fetchDepartmentPersonnel = async (): Promise<DepartmentPersonnel[]> => {
   try {
    const response = await ebitaFetcher.get(APIUrls.Dashboard.DepartmentSummary)
    const data = response.data
    return data
    } catch (error) {
    console.error('Error fetching dashboard data:', error)
    throw error
  }
  };
